import { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Phone, MessageSquare, Monitor, MapPin, Loader2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { cn, getButtonColor } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

export default function TeacherParentUpdates() {
  const navigate = useNavigate();
  const { teacherTheme } = useTeacher();
  const isDark = teacherTheme === 'dark';
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('progress-report');
  const [selectedReportType, setSelectedReportType] = useState('ai');
  
  // Form states
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [reportType, setReportType] = useState('weekly');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingMode, setMeetingMode] = useState('in-person');
  const [messageContent, setMessageContent] = useState(''); // For progress report
  const [announcementContent, setAnnouncementContent] = useState(''); // For meeting announcements
  const [deliveryOptions, setDeliveryOptions] = useState({
    whatsapp: true,
    email: true,
    sms: true
  });
  
  // UI States
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSendConfirmOpen, setIsSendConfirmOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  
  // Effect to populate AI-generated content when AI report type is selected
  useEffect(() => {
    // Only run this effect when in the progress report tab
    if (activeTab !== 'progress-report') return;
    
    // Check if "All Students" is selected
    const isAllStudents = selectedStudent === '1' || selectedStudent === '';
    
    if (selectedReportType === 'ai') {
      if (isAllStudents) {
        // Simulate AI-generated content for all students
        const aiContent = `[AI-Generated Class Performance Report]

Class: Grade 7A
Period: Week of ${new Date().toLocaleDateString()}

Overall Class Performance:
- Mathematics: Strong analytical skills across 85% of students
- Science: Good grasp of concepts, needs improvement in lab work for 30% of students
- English: Active participation in discussions, writing skills developing well

Class Participation & Engagement:
- Overall attendance: 95%
- Active contributors: 70% of students
- Leadership qualities observed in 25% of students

Common Areas for Growth:
- Time management during exams
- Attention to detail in written assignments
- Peer collaboration skills

Class Recommendations:
- Implement peer tutoring sessions for struggling students
- Organize group projects to enhance collaboration
- Continue with current reading habits to improve vocabulary

Individual Highlights:
- Arjun Sharma: Exceptional performance in mathematics
- Priya Patel: Outstanding creativity in art projects
- Rahul Gupta: Significant improvement in science grades`;
        
        setMessageContent(aiContent);
      } else {
        // Simulate AI-generated content for individual student
        const aiContent = `[AI-Generated Student Performance Report]

Student: Arjun Sharma
Period: Week of ${new Date().toLocaleDateString()}

Academic Performance:
- Mathematics: Strong analytical skills, consistently scores above 90%
- Science: Good grasp of concepts, needs improvement in lab work
- English: Active participant in discussions, writing skills developing well

Participation & Engagement:
- Regular attendance (98% this month)
- Actively contributes to group activities
- Shows leadership qualities during team projects

Areas for Growth:
- Time management during exams
- Attention to detail in written assignments

Recommendations:
- Encourage participation in advanced math competitions
- Suggest joining the science club for hands-on experience
- Continue with current reading habits to improve vocabulary`;
        
        setMessageContent(aiContent);
      }
    } else if (selectedReportType === 'combined') {
      if (isAllStudents) {
        // Combined report for all students
        const aiContent = `[AI-Generated Class Performance Report]

Class: Grade 7A
Period: Week of ${new Date().toLocaleDateString()}

Overall Class Performance:
- Mathematics: Strong analytical skills across 85% of students
- Science: Good grasp of concepts, needs improvement in lab work for 30% of students
- English: Active participation in discussions, writing skills developing well

Class Participation & Engagement:
- Overall attendance: 95%
- Active contributors: 70% of students
- Leadership qualities observed in 25% of students

Common Areas for Growth:
- Time management during exams
- Attention to detail in written assignments
- Peer collaboration skills

Class Recommendations:
- Implement peer tutoring sessions for struggling students
- Organize group projects to enhance collaboration
- Continue with current reading habits to improve vocabulary

---

[Your Personal Observations:]
- `;
        
        setMessageContent(aiContent);
      } else {
        // Combined report with AI content and space for manual additions for individual student
        const aiContent = `[AI-Generated Student Performance Report]

Student: Arjun Sharma
Period: Week of ${new Date().toLocaleDateString()}

Academic Performance:
- Mathematics: Strong analytical skills, consistently scores above 90%
- Science: Good grasp of concepts, needs improvement in lab work
- English: Active participant in discussions, writing skills developing well

Participation & Engagement:
- Regular attendance (98% this month)
- Actively contributes to group activities
- Shows leadership qualities during team projects

Areas for Growth:
- Time management during exams
- Attention to detail in written assignments

Recommendations:
- Encourage participation in advanced math competitions
- Suggest joining the science club for hands-on experience
- Continue with current reading habits to improve vocabulary

---

[Your Personal Observations:]
- `;
        
        setMessageContent(aiContent);
      }
    } else if (selectedReportType === 'manual') {
      if (isAllStudents) {
        // Template for manual report for all students
        setMessageContent(`[Class Performance Report Template]

Class: Grade 7A
Period: Week of ${new Date().toLocaleDateString()}

Overall Class Observations:
- 

Subject-wise Performance:
- Mathematics: 
- Science: 
- English: 

Classroom Dynamics:
- 

Recommendations for Class Improvement:
- 

Parent Communication Points:
- `);
      } else {
        // Template for manual report for individual student
        setMessageContent(`[Manual Report Template]

Student: Arjun Sharma
Period: Week of ${new Date().toLocaleDateString()}

Academic Observations:
- 

Behavioral Notes:
- 

Recommendations:
- 

Parent Communication Points:
- `);
      }
    }
  }, [selectedReportType, activeTab, selectedStudent]);
  
  // Function to update announcement content with current form values
  const updateAnnouncementContent = () => {
    const formattedDate = meetingDate ? new Date(meetingDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '[Meeting Date]';
    const formattedTime = meetingTime ? new Date(`2000-01-01T${meetingTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '[Meeting Time]';
    const modeText = meetingMode === 'online' ? 'Online (Link will be provided)' : 'In-person (Location will be provided)';
    const titleText = meetingTitle || '[Meeting Title]';
    
    setAnnouncementContent(`Dear Parents,

We would like to invite you to the upcoming Parent-Teacher Meeting for your child.

Meeting: ${titleText}

Details:
- Date: ${formattedDate}
- Time: ${formattedTime}
- Mode: ${modeText}

Agenda:
- Academic progress review
- Behavioral observations
- Discussion on improvement strategies

Please confirm your availability.

Best regards,
[Teacher Name]`);
  };
  
  // Effect to initialize announcement content when switching to meeting announcement tab
  useEffect(() => {
    if (activeTab === 'meeting-announcement' && !announcementContent) {
      updateAnnouncementContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  // Effect to update announcement content when meeting details change
  useEffect(() => {
    if (activeTab === 'meeting-announcement') {
      updateAnnouncementContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingDate, meetingTime, meetingMode, meetingTitle]);
  
  // Mock data
  const classes = [
    { id: '1', name: 'Grade 7A' },
    { id: '2', name: 'Grade 7B' },
    { id: '3', name: 'Grade 8A' },
    { id: '4', name: 'Grade 8B' }
  ];
  
  const students = [
    { id: '1', name: 'All Students in Class' },
    { id: '2', name: 'Arjun Sharma' },
    { id: '3', name: 'Priya Patel' },
    { id: '4', name: 'Rahul Gupta' }
  ];
  
  const handleDeliveryOptionChange = (option) => {
    setDeliveryOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };
  
  // Validation function
  const validateForm = () => {
    if (activeTab === 'progress-report') {
      if (!selectedClass) {
        toast({
          title: "Validation Error",
        description: "Please select a class.",
          variant: "destructive",
        });
        return false;
      }
      if (!selectedStudent) {
        toast({
          title: "Validation Error",
          description: "Please select a student.",
          variant: "destructive",
        });
        return false;
      }
      if (!messageContent.trim()) {
        toast({
          title: "Validation Error",
          description: "Please add content to the report.",
          variant: "destructive",
        });
        return false;
      }
      if (!deliveryOptions.whatsapp && !deliveryOptions.email && !deliveryOptions.sms) {
        toast({
          title: "Validation Error",
          description: "Please select at least one delivery option.",
          variant: "destructive",
        });
        return false;
      }
    } else {
      if (!selectedClass) {
        toast({
          title: "Validation Error",
          description: "Please select a class.",
          variant: "destructive",
        });
        return false;
      }
      if (!meetingTitle.trim()) {
        toast({
          title: "Validation Error",
          description: "Please enter a meeting title.",
          variant: "destructive",
        });
        return false;
      }
      if (!meetingDate) {
        toast({
          title: "Validation Error",
          description: "Please select a meeting date.",
          variant: "destructive",
        });
        return false;
      }
      if (!meetingTime) {
        toast({
          title: "Validation Error",
          description: "Please select a meeting time.",
          variant: "destructive",
        });
        return false;
      }
      if (!announcementContent.trim()) {
        toast({
          title: "Validation Error",
          description: "Please add content to the announcement.",
          variant: "destructive",
        });
        return false;
      }
      if (!deliveryOptions.whatsapp && !deliveryOptions.email && !deliveryOptions.sms) {
        toast({
          title: "Validation Error",
          description: "Please select at least one delivery option.",
          variant: "destructive",
        });
        return false;
      }
    }
    return true;
  };
  
  const handlePreview = () => {
    if (!validateForm()) return;
    setIsPreviewOpen(true);
    try { 
      trackEvent('teacher_parent_update_preview', attachUserProps({ 
        page: activeTab, 
        report_type: selectedReportType, 
        student_id: selectedStudent || 'all', 
        class_id: selectedClass 
      })); 
    } catch(e) {}
  };
  
  const handleSend = () => {
    if (!validateForm()) return;
    setIsSendConfirmOpen(true);
  };
  
  const confirmSend = async () => {
    setIsSendConfirmOpen(false);
    setIsSending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const selectedChannels = [];
    if (deliveryOptions.whatsapp) selectedChannels.push('WhatsApp');
    if (deliveryOptions.email) selectedChannels.push('Email');
    if (deliveryOptions.sms) selectedChannels.push('SMS');
    
    setIsSending(false);
    
    toast({
      title: activeTab === 'progress-report' ? "Progress Report Sent" : "Announcement Sent",
      description: `Successfully sent via ${selectedChannels.join(', ')} to parents.`,
    });
    
    // Reset form after successful send

      try { /* no-op to keep lint happy */ } catch(e) {}

      // Track send event (after simulated send)
      const trackSendEvent = (channels) => {
        try { 
            trackEvent('teacher_parent_update_sent', attachUserProps({ 
                page: activeTab, 
                report_type: selectedReportType, 
                student_id: selectedStudent || 'all', 
                class_id: selectedClass, 
                channels: channels 
            })); 
        } catch(e) {}
      };
    if (activeTab === 'progress-report') {
      setMessageContent('');
      setSelectedClass('');
      setSelectedStudent('');
    } else {
      setAnnouncementContent('');
      setMeetingTitle('');
      setMeetingDate('');
      setMeetingTime('');
      setSelectedClass('');
    }
    // Track send
    trackSendEvent(selectedChannels);
  };
  
  const handleSaveDraft = async () => {
    if (activeTab === 'progress-report' && !messageContent.trim()) {
      toast({
        title: "Cannot Save Draft",
        description: "Please add some content before saving as draft.",
        variant: "destructive",
      });
      return;
    }
    if (activeTab === 'meeting-announcement' && !announcementContent.trim()) {
      toast({
        title: "Cannot Save Draft",
        description: "Please add some content before saving as draft.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSavingDraft(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Save to localStorage
    const draftKey = `parent-update-draft-${activeTab}`;
    const draftData = {
      activeTab,
      selectedClass,
      selectedStudent,
      reportType,
      selectedReportType,
      messageContent,
      meetingTitle,
      meetingDate,
      meetingTime,
      meetingMode,
      announcementContent,
      deliveryOptions,
      savedAt: new Date().toISOString(),
    };
    
    localStorage.setItem(draftKey, JSON.stringify(draftData));
    
    setIsSavingDraft(false);
    
    toast({
      title: activeTab === 'progress-report' ? "Report Draft Saved" : "Announcement Draft Saved",
      description: "Your draft has been saved successfully. You can continue editing later.",
    });
    try { 
        trackEvent('teacher_parent_update_saved_draft', attachUserProps({ 
            page: activeTab, 
            report_type: selectedReportType, 
            class_id: selectedClass, 
            student_id: selectedStudent || 'all' 
        })); 
    } catch(e) {}
  };
  
  // Load draft on component mount
  useEffect(() => {
    const draftKey = `parent-update-draft-${activeTab}`;
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        // Only load if draft is recent (within 7 days)
        const draftDate = new Date(draftData.savedAt);
        const daysSince = (new Date() - draftDate) / (1000 * 60 * 60 * 24);
        if (daysSince < 7) {
          setSelectedClass(draftData.selectedClass || '');
          setSelectedStudent(draftData.selectedStudent || '');
          setReportType(draftData.reportType || 'weekly');
          setSelectedReportType(draftData.selectedReportType || 'ai');
          setMessageContent(draftData.messageContent || '');
          setMeetingTitle(draftData.meetingTitle || '');
          setMeetingDate(draftData.meetingDate || '');
          setMeetingTime(draftData.meetingTime || '');
          setMeetingMode(draftData.meetingMode || 'in-person');
          setAnnouncementContent(draftData.announcementContent || '');
          setDeliveryOptions(draftData.deliveryOptions || { whatsapp: true, email: true, sms: true });
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [activeTab]);
  
  // Get formatted preview content
  const getPreviewContent = () => {
    if (activeTab === 'progress-report') {
      return messageContent;
    } else {
      return announcementContent;
    }
  };
  
  // Get preview title
  const getPreviewTitle = () => {
    if (activeTab === 'progress-report') {
      const studentName = students.find(s => s.id === selectedStudent)?.name || 'All Students';
      const className = classes.find(c => c.id === selectedClass)?.name || 'Selected Class';
      return `Progress Report - ${studentName} (${className})`;
    } else {
      return `Meeting Announcement - ${meetingTitle || 'Untitled'}`;
    }
  };
  
  return (
    <div className={cn("flex flex-col h-full p-8 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/teacher/dashboard')}
              className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/60" : "text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EBE3]/60")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={cn("text-4xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Parent Updates</h1>
              <p className={cn("mt-1 text-base font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Send important updates to parents through WhatsApp, Email, or SMS</p>
            </div>
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="flex gap-2 w-full">
          <button
            onClick={() => setActiveTab('progress-report')}
            className={cn(
              "flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 h-10 shadow-sm",
              activeTab === 'progress-report'
                ? getButtonColor(isDark)
                : isDark
                  ? "bg-[hsl(var(--card-border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/80"
                  : "bg-[#E0D4BF] text-[#1A1A1A] hover:bg-[#D4C4A8]"
            )}
          >
            Student Progress Report
          </button>
          
          <button
            onClick={() => setActiveTab('meeting-announcement')}
            className={cn(
              "flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 h-10 shadow-sm",
              activeTab === 'meeting-announcement'
                ? getButtonColor(isDark)
                : isDark
                  ? "bg-[hsl(var(--card-border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/80"
                  : "bg-[#E0D4BF] text-[#1A1A1A] hover:bg-[#D4C4A8]"
            )}
          >
            Parent-Teacher Meeting Announcements
          </button>
        </div>
        
        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'progress-report' ? (
            <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border-[#E0D4BF]")}>
              <CardHeader className={cn("py-5 border-b rounded-t-2xl", isDark ? "bg-[hsl(var(--card-border))]/10 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                <CardTitle className={cn("text-xl font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Student Progress Report</CardTitle>
                <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Send weekly or monthly performance updates to parents</p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column - Form Fields */}
                  <div className="space-y-6">
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Select Class</label>
                      <Select value={selectedClass} onValueChange={setSelectedClass}>
                        <SelectTrigger className={cn("w-full", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                          <SelectValue placeholder="Choose a class" />
                        </SelectTrigger>
                        <SelectContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                          {classes.map(cls => (
                            <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Select Student</label>
                      <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                        <SelectTrigger className={cn("w-full", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                          <SelectValue placeholder="Choose a student" />
                        </SelectTrigger>
                        <SelectContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                          {students.map(student => (
                            <SelectItem key={student.id} value={student.id}>{student.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Report Type</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setReportType('weekly')}
                          className={cn(
                            "py-3 px-4 rounded-lg border text-sm font-medium transition-all h-10",
                            reportType === 'weekly'
                              ? getButtonColor(isDark)
                              : isDark
                                ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]"
                                : "bg-white border-[#E0D4BF] text-[#2B2B2B]"
                          )}
                        >
                          Weekly
                        </button>
                        <button
                          onClick={() => setReportType('monthly')}
                          className={cn(
                            "py-3 px-4 rounded-lg border text-sm font-medium transition-all h-10",
                            reportType === 'monthly'
                              ? getButtonColor(isDark)
                              : isDark
                                ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]"
                                : "bg-white border-[#E0D4BF] text-[#2B2B2B]"
                          )}
                        >
                          Monthly
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Delivery Options</label>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <MessageSquare className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                            <span className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>WhatsApp</span>
                          </div>
                          <button
                            onClick={() => handleDeliveryOptionChange('whatsapp')}
                            className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", deliveryOptions.whatsapp ? (isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]") : "bg-[#D4C4A8]")}
                          >
                            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", deliveryOptions.whatsapp ? "translate-x-6" : "translate-x-1")} />
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Mail className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                            <span className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Email</span>
                          </div>
                          <button
                            onClick={() => handleDeliveryOptionChange('email')}
                            className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", deliveryOptions.email ? (isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]") : "bg-[#D4C4A8]")}
                          >
                            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", deliveryOptions.email ? "translate-x-6" : "translate-x-1")} />
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Phone className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                            <span className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>SMS</span>
                          </div>
                          <button
                            onClick={() => handleDeliveryOptionChange('sms')}
                            className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", deliveryOptions.sms ? (isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]") : "bg-[#D4C4A8]")}
                          >
                            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", deliveryOptions.sms ? "translate-x-6" : "translate-x-1")} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column - Report Type Selection with Dropdown */}
                  <div className="space-y-6">
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Report Type</label>
                      
                      <Select value={selectedReportType} onValueChange={setSelectedReportType}>
                        <SelectTrigger className={cn("w-full", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                          <SelectValue placeholder="Choose report type" />
                        </SelectTrigger>
                        <SelectContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                          <SelectItem value="ai">AI-Generated Report</SelectItem>
                          <SelectItem value="manual">Manual Report</SelectItem>
                          <SelectItem value="combined">Combined Report (AI + Manual)</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <p className={cn("text-sm mt-2", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                        {selectedReportType === 'ai' && "Automatically generated insights based on student activity and performance"}
                        {selectedReportType === 'manual' && "Write your own personalized assessment and observations"}
                        {selectedReportType === 'combined' && "AI-generated insights with your personal observations and validation"}
                      </p>
                    </div>
                    
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Report Content</label>
                      <textarea
                        placeholder="Select a report type above to populate this area with content..."
                        className={cn(
                          "w-full h-64 rounded-xl p-4 text-sm border resize-none",
                          isDark 
                            ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]" 
                            : "bg-white border-[#E0D4BF] text-[#2B2B2B] placeholder:text-[#6B6B6B]"
                        )}
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                      />
                      <p className={cn("text-xs mt-2", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                        {selectedReportType === 'ai' && (selectedStudent === '1' || selectedStudent === '') 
                          ? "This AI-generated class report is pre-populated. You can edit or add to it before sending." 
                          : selectedReportType === 'ai' 
                            ? "This AI-generated report is pre-populated. You can edit or add to it before sending."
                            : selectedReportType === 'manual' && (selectedStudent === '1' || selectedStudent === '')
                              ? "This is a blank template for your class report. Feel free to write your own content."
                              : selectedReportType === 'manual'
                                ? "This is a blank template for your manual report. Feel free to write your own content."
                                : selectedReportType === 'combined' && (selectedStudent === '1' || selectedStudent === '')
                                  ? "This report combines AI insights for the class with space for your personal observations. Edit as needed."
                                  : "This report combines AI insights with space for your personal observations. Edit as needed."
                        }
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-dashed" style={{ borderColor: isDark ? 'hsl(var(--card-border))' : '#E0D4BF' }}>
                  <Button
                    onClick={handlePreview}
                    disabled={isSending || isSavingDraft}
                    className={cn("px-6 h-10 font-medium rounded-lg shadow-md", getButtonColor(isDark))}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Report
                  </Button>
                  <Button
                    onClick={handleSaveDraft}
                    disabled={isSending || isSavingDraft}
                    className={cn("px-6 h-10 font-medium rounded-lg shadow-md", getButtonColor(isDark))}
                  >
                    {isSavingDraft ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save as Draft'
                    )}
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={isSending || isSavingDraft}
                    className={cn("px-6 h-10 font-medium rounded-lg shadow-md", getButtonColor(isDark))}
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send to Parent'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border-[#E0D4BF]")}>
              <CardHeader className={cn("py-5 border-b rounded-t-2xl", isDark ? "bg-[hsl(var(--card-border))]/10 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                <CardTitle className={cn("text-xl font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Parent-Teacher Meeting Announcements</CardTitle>
                <p className={cn("text-sm mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Notify parents about PTM schedules or meetings</p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column - Form Fields */}
                  <div className="space-y-6">
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Select Class / Section</label>
                      <Select value={selectedClass} onValueChange={setSelectedClass}>
                        <SelectTrigger className={cn("w-full", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                          <SelectValue placeholder="Choose a class" />
                        </SelectTrigger>
                        <SelectContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                          {classes.map(cls => (
                            <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Meeting Title</label>
                      <Input
                        placeholder="e.g., Monthly PTM - Class 9"
                        value={meetingTitle}
                        onChange={(e) => setMeetingTitle(e.target.value)}
                        className={cn(
                          "w-full",
                          isDark 
                            ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" 
                            : "bg-white border-[#E0D4BF] text-[#2B2B2B]"
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Date</label>
                        <Input
                          type="date"
                          value={meetingDate}
                          onChange={(e) => setMeetingDate(e.target.value)}
                          className={cn(
                            "w-full",
                            isDark 
                              ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" 
                              : "bg-white border-[#E0D4BF] text-[#2B2B2B]"
                          )}
                        />
                      </div>
                      
                      <div>
                        <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Time</label>
                        <Input
                          type="time"
                          value={meetingTime}
                          onChange={(e) => setMeetingTime(e.target.value)}
                          className={cn(
                            "w-full",
                            isDark 
                              ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" 
                              : "bg-white border-[#E0D4BF] text-[#2B2B2B]"
                          )}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Mode</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setMeetingMode('in-person')}
                          className={cn(
                            "py-3 px-4 rounded-lg border text-sm font-medium transition-all flex items-center justify-center gap-2 h-10",
                            meetingMode === 'in-person'
                              ? getButtonColor(isDark)
                              : isDark
                                ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]"
                                : "bg-white border-[#E0D4BF] text-[#2B2B2B]"
                          )}
                        >
                          <MapPin className="w-4 h-4" />
                          In-person
                        </button>
                        <button
                          onClick={() => setMeetingMode('online')}
                          className={cn(
                            "py-3 px-4 rounded-lg border text-sm font-medium transition-all flex items-center justify-center gap-2 h-10",
                            meetingMode === 'online'
                              ? getButtonColor(isDark)
                              : isDark
                                ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]"
                                : "bg-white border-[#E0D4BF] text-[#2B2B2B]"
                          )}
                        >
                          <Monitor className="w-4 h-4" />
                          Online
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Delivery Options</label>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <MessageSquare className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                            <span className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>WhatsApp</span>
                          </div>
                          <button
                            onClick={() => handleDeliveryOptionChange('whatsapp')}
                            className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", deliveryOptions.whatsapp ? (isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]") : "bg-[#D4C4A8]")}
                          >
                            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", deliveryOptions.whatsapp ? "translate-x-6" : "translate-x-1")} />
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Mail className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                            <span className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Email</span>
                          </div>
                          <button
                            onClick={() => handleDeliveryOptionChange('email')}
                            className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", deliveryOptions.email ? (isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]") : "bg-[#D4C4A8]")}
                          >
                            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", deliveryOptions.email ? "translate-x-6" : "translate-x-1")} />
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Phone className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                            <span className={cn("font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>SMS</span>
                          </div>
                          <button
                            onClick={() => handleDeliveryOptionChange('sms')}
                            className={cn("relative inline-flex h-6 w-11 items-center rounded-full transition-colors", deliveryOptions.sms ? (isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#2B2B2B]") : "bg-[#D4C4A8]")}
                          >
                            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform", deliveryOptions.sms ? "translate-x-6" : "translate-x-1")} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column - Message Editor */}
                  <div className="space-y-6">
                    <div>
                      <label className={cn("block text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Short Message Editor</label>
                      <textarea
                        placeholder="Write your message here..."
                        className={cn(
                          "w-full h-full min-h-[300px] rounded-xl p-4 text-sm border resize-none",
                          isDark 
                            ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]" 
                            : "bg-white border-[#E0D4BF] text-[#2B2B2B] placeholder:text-[#6B6B6B]"
                        )}
                        value={announcementContent}
                        onChange={(e) => setAnnouncementContent(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-dashed" style={{ borderColor: isDark ? 'hsl(var(--card-border))' : '#E0D4BF' }}>
                  <Button
                    onClick={handlePreview}
                    disabled={isSending || isSavingDraft}
                    className={cn("px-6 h-10 font-medium rounded-lg shadow-md", getButtonColor(isDark))}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Message
                  </Button>
                  <Button
                    onClick={handleSaveDraft}
                    disabled={isSending || isSavingDraft}
                    className={cn("px-6 h-10 font-medium rounded-lg shadow-md", getButtonColor(isDark))}
                  >
                    {isSavingDraft ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save as Draft'
                    )}
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={isSending || isSavingDraft}
                    className={cn("px-6 h-10 font-medium rounded-lg shadow-md", getButtonColor(isDark))}
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Announcement'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className={cn("max-w-3xl max-h-[90vh] overflow-y-auto", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
          <DialogHeader>
            <DialogTitle className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
              {getPreviewTitle()}
            </DialogTitle>
            <DialogDescription className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
              Review your {activeTab === 'progress-report' ? 'progress report' : 'announcement'} before sending
            </DialogDescription>
          </DialogHeader>
          <div className={cn("mt-4 p-4 rounded-lg border", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
            <div className={cn("whitespace-pre-wrap text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
              {getPreviewContent()}
            </div>
          </div>
          <div className="mt-4">
            <p className={cn("text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Delivery Channels:</p>
            <div className="flex gap-3">
              {deliveryOptions.whatsapp && (
                <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg", isDark ? "bg-[hsl(var(--card-border))]/30" : "bg-[#E0D4BF]/30")}>
                  <MessageSquare className={cn("w-4 h-4", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                  <span className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>WhatsApp</span>
                </div>
              )}
              {deliveryOptions.email && (
                <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg", isDark ? "bg-[hsl(var(--card-border))]/30" : "bg-[#E0D4BF]/30")}>
                  <Mail className={cn("w-4 h-4", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                  <span className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Email</span>
                </div>
              )}
              {deliveryOptions.sms && (
                <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg", isDark ? "bg-[hsl(var(--card-border))]/30" : "bg-[#E0D4BF]/30")}>
                  <Phone className={cn("w-4 h-4", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                  <span className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>SMS</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsPreviewOpen(false)}
              className={cn("px-6", isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setIsPreviewOpen(false);
                handleSend();
              }}
              className={cn("px-6", getButtonColor(isDark))}
            >
              Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Send Confirmation Dialog */}
      <AlertDialog open={isSendConfirmOpen} onOpenChange={setIsSendConfirmOpen}>
        <AlertDialogContent className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
          <AlertDialogHeader>
            <AlertDialogTitle className={cn("text-xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>
              Confirm Send
            </AlertDialogTitle>
            <AlertDialogDescription className={cn("text-sm", isDark ? "text-white" : "text-[#6B6B6B]")}>
              Are you sure you want to send this {activeTab === 'progress-report' ? 'progress report' : 'announcement'} to parents?
              <br />
              <br />
              <span className="font-medium">Delivery Channels:</span>
              <div className="mt-2 space-y-1">
                {deliveryOptions.whatsapp && <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> WhatsApp</div>}
                {deliveryOptions.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</div>}
                {deliveryOptions.sms && <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> SMS</div>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={cn(isDark ? "border-[hsl(var(--card-border))] text-white hover:text-white" : "border-[#E0D4BF]")}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSend}
              className={cn(getButtonColor(isDark))}
            >
              Yes, Send Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}